import { readLocal, writeLocal } from '../local-store'
import { simulate } from '../mock-utils'
import { acompanantesMock } from '../acompanantes/mock'
import { featureEnabled } from '@/config/features'
import { ServiceError } from '../errors'
import { money } from '../types'
import type { CatalogOption } from '../reserva/types'
import { buildOccupants } from './occupants'
import { ageOn } from './rules'
import type { Arrival, ArrivalOptions, CheckinService, ContractSummary } from './types'

export const ARRIVAL_FIELD = 'arrival'
/** Personas registradas en el mock: el mock de reserva las suma a la reserva para que el registro se vea hecho. */
export const MOCK_REGISTERED_FIELD = 'mockRegistered'
export interface MockRegistered {
  id: string
  name: string
  surname: string
  isAdult: boolean
  isHolder: boolean
  email: string
}
const PHONE_FIELD = 'phone'

/** Marta llega el sábado entre las 16:00 y las 18:00, en metro (Inicio.dc.html). El hotel aún no la ha elegido. */
const DEMO_ARRIVAL: Arrival = { date: '2026-10-03', etaSlot: { from: '16:00', to: '18:00' }, transport: 'plane_metro', editableUntil: '2026-10-03T12:00:00+02:00' }

/** Franjas del paso 6 (CheckIn.dc.html, 2h) */
const SLOTS: ArrivalOptions['slots'] = [
  { from: '14:00', to: '16:00' },
  { from: '16:00', to: '18:00' },
  { from: '18:00', to: '20:00' },
  { from: '20:00', to: '22:00' },
  { from: '22:00', to: null },
]

/** "Entrar desde las 13:00 · 20,00 € · se cancela gratis hasta el día antes" (CheckIn.dc.html, 2h) */
const EARLY = { available: true, from: '13:00', price: money(2000), cancelFreeUntil: '2026-10-02T23:59:00+02:00' }

/** Normas en cinco puntos (CheckIn.dc.html, 2i) */
const SUMMARY_DEMO: ContractSummary = {
  points: [
    'Máximo 3 personas alojadas, las registradas.',
    'Silencio de 22:00 a 8:00. No se permiten fiestas.',
    'Prohibido fumar dentro y en la terraza.',
    'Fianza de 300 € apartada en tu tarjeta; se libera al salir si no hay daños.',
    'Salida antes de las 11:00 del martes 6.',
  ],
}
const SUMMARY_HOTEL: ContractSummary = {
  points: ['Máximo 2 personas en la habitación.', 'Desayuno de 7:30 a 10:30.', 'Piscina de 10:00 a 20:00.', 'Prohibido fumar en todo el hotel.', 'Salida antes de las 12:00.'],
}

/** Fechas de registro que la API no devuelve; el bundle las muestra ("Registrada el 14 sep"). */
const REGISTERED_AT: Record<string, string> = { T1: '2026-09-12T19:40:00+02:00', T2: '2026-09-14T09:05:00+02:00' }

const MUNICIPALITIES: Record<string, CatalogOption[]> = {
  P08: [
    { id: 'M080193', name: 'Barcelona', value: 'Barcelona' },
    { id: 'M081017', name: "L'Hospitalet de Llobregat", value: "L'Hospitalet de Llobregat" },
    { id: 'M082009', name: 'Badalona', value: 'Badalona' },
  ],
  P28: [
    { id: 'M280796', name: 'Madrid', value: 'Madrid' },
    { id: 'M280058', name: 'Alcalá de Henares', value: 'Alcalá de Henares' },
    { id: 'M281614', name: 'Collado Villalba', value: 'Collado Villalba' },
  ],
  P04: [
    { id: 'M040130', name: 'Almería', value: 'Almería' },
    { id: 'M040790', name: 'Roquetas de Mar', value: 'Roquetas de Mar' },
  ],
  P46: [{ id: 'M462508', name: 'València', value: 'València' }],
}

let savedSeq = 0

/**
 * Registro simulado con los datos del bundle (inventario D4). Además de los códigos de mock-utils:
 * `guardado-falla` → el guardado del registro falla (paso 5); `firma-falla` → falla la firma (paso 7).
 */
export const checkinMock: CheckinService = {
  notices: { phoneReachesProperty: true, contractByEmail: true },
  async getArrival(stay, signal) {
    await simulate(stay.guestCode, 'checkin', signal)
    const local = readLocal<Arrival>(stay.guestCode, ARRIVAL_FIELD)
    if (local) return local
    return stay.guestCode.includes('hotel') || stay.guestCode.includes('titular-pendiente') ? null : DEMO_ARRIVAL
  },
  async setArrival(stay, input) {
    await simulate(stay.guestCode, 'checkin')
    const arrival: Arrival = { date: stay.dates.checkIn.slice(0, 10), etaSlot: input.etaSlot, transport: input.transport, editableUntil: stay.dates.checkIn }
    writeLocal(stay.guestCode, ARRIVAL_FIELD, arrival)
    return arrival
  },
  async getArrivalOptions(stay, signal) {
    await simulate(stay.guestCode, 'checkin', signal)
    // La oferta se muestra en el paso 6; se contrata con el cambio de reserva (D47), no con este servicio.
    return { slots: SLOTS, earlyCheckin: stay.guestCode.includes('hotel') ? null : { ...EARLY, added: false } }
  },

  async getOccupants(stay, signal) {
    await simulate(stay.guestCode, 'checkin', signal)
    if (!featureEnabled('invitados')) return buildOccupants(stay, [], REGISTERED_AT, [])
    const [invitations, revoked] = await Promise.all([acompanantesMock.list(stay, signal).catch(() => []), acompanantesMock.listRevoked(stay, signal).catch(() => [])])
    return buildOccupants(stay, invitations, REGISTERED_AT, revoked)
  },
  async getMunicipalities(stay, provinceId, signal) {
    await simulate(stay.guestCode, 'checkin', signal)
    return MUNICIPALITIES[provinceId] ?? []
  },
  async saveGuest(stay, guest) {
    await simulate(stay.guestCode, 'checkin')
    if (stay.guestCode.includes('guardado-falla')) throw new ServiceError('server', 'mock save error')
    savedSeq += 1
    const id = `T-NEW-${Date.now()}-${savedSeq}`
    const registered = readLocal<MockRegistered[]>(stay.guestCode, MOCK_REGISTERED_FIELD) ?? []
    const isHolder = stay.registration.holderPending && !registered.some((r) => r.isHolder)
    const age = ageOn(guest.bornDate)
    writeLocal(stay.guestCode, MOCK_REGISTERED_FIELD, [...registered, { id, name: guest.name, surname: [guest.surname1, guest.surname2].filter(Boolean).join(' '), isAdult: age === undefined || age >= 18, isHolder, email: guest.email }])
    return { travelerId: id, documents: [{ name: 'Contrato de alojamiento', url: 'https://example.invalid/contrato.pdf', type: 'PDF' }] }
  },
  async completeGuest(stay, travelerId) {
    await simulate(stay.guestCode, 'checkin')
    if (stay.guestCode.includes('guardado-falla')) throw new ServiceError('server', 'mock save error')
    return { travelerId, documents: [] }
  },
  async saveSignature(stay) {
    await simulate(stay.guestCode, 'checkin')
    if (stay.guestCode.includes('firma-falla')) throw new ServiceError('server', 'mock signature error')
  },

  async getPhone(stay) {
    await simulate(stay.guestCode, 'checkin')
    return readLocal<string>(stay.guestCode, PHONE_FIELD)
  },
  async setPhone(stay, phone) {
    await simulate(stay.guestCode, 'checkin')
    writeLocal(stay.guestCode, PHONE_FIELD, phone)
  },
  async getContractSummary(stay, signal) {
    await simulate(stay.guestCode, 'checkin', signal)
    return stay.guestCode.includes('hotel') ? SUMMARY_HOTEL : SUMMARY_DEMO
  },
  async sendContractBeforeSigning(stay) {
    await simulate(stay.guestCode, 'checkin')
  },
}
